import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Menu } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import { useAppDispatch, useAppSelector } from "@/app/store";
import {
  selectSidebarOpen,
  selectDirection,
  setSidebarOpen,
} from "@/app/store/slices/ui.slice";
import { selectUser, selectUserPermissions } from "@/app/store/slices/auth.slice";
import {
  navigationConfig,
  bottomNavigationConfig,
  filterSectionsByPermissions,
  filterNavByPermissions,
  isBranchAccountUser,
} from "../config/navigation.config";
import { SidebarProvider } from "./Sidebar/SidebarContext";
import { SidebarSection } from "./Sidebar/SidebarSection";
import { SidebarItem } from "./Sidebar/SidebarItem";
import { SidebarLogo } from "./Sidebar/SidebarLogo";

export function MobileDrawer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isOpen = useAppSelector(selectSidebarOpen);
  const direction = useAppSelector(selectDirection);
  const user = useAppSelector(selectUser);
  const userPermissions = useAppSelector(selectUserPermissions);
  const isBranchUser = isBranchAccountUser(user);

  const filteredSections = filterSectionsByPermissions(
    navigationConfig,
    userPermissions,
    isBranchUser,
  );
  const filteredBottomNav = filterNavByPermissions(
    bottomNavigationConfig,
    userPermissions,
    isBranchUser,
  );

  // Close drawer on route change
  useEffect(() => {
    dispatch(setSidebarOpen(false));
  }, [location.pathname, dispatch]);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dispatch(setSidebarOpen(false));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  const handleClose = () => dispatch(setSidebarOpen(false));
  const handleToggle = () => dispatch(setSidebarOpen(!isOpen));

  return (
    <SidebarProvider>
      {/* Toggle Button - Always visible on mobile */}
      <button
        onClick={handleToggle}
        className={cn(
          "fixed top-4 z-50 p-2 rounded-lg",
          "bg-grey-900 border border-grey-700 shadow-lg",
          "text-white hover:bg-grey-800",
          "transition-colors",
          direction === "rtl" ? "right-4" : "left-4"
        )}
        aria-label={isOpen ? t("common.close") : "Menu"}
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-fade-in"
          onClick={handleClose}
        />
      )}

      {/* Drawer */}
      <aside
        className={cn(
          "fixed top-0 bottom-0 z-50 w-[280px]",
          "bg-grey-50 border-grey-100",
          "flex flex-col",
          "transition-transform duration-300 ease-in-out",
          direction === "rtl"
            ? `right-0 border-l ${
                isOpen ? "translate-x-0" : "translate-x-full"
              }`
            : `left-0 border-r ${
                isOpen ? "translate-x-0" : "-translate-x-full"
              }`
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <SidebarLogo isOpen />
          <button
            onClick={handleClose}
            className="p-2 rounded-md"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 custom-scrollbar">
          {filteredSections.map((section) => (
            <SidebarSection
              key={section.id}
              section={section}
              isOpen
              currentPath={location.pathname}
            />
          ))}
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-sidebar-border py-2 px-2">
          {filteredBottomNav.map((item) => (
            <SidebarItem
              key={item.id}
              item={item}
              isOpen
              isActive={location.pathname === item.href}
              isBottomItem
            />
          ))}
        </div>
      </aside>
    </SidebarProvider>
  );
}
